import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { SpinnerGap } from "@phosphor-icons/react";

const inputCls =
  "mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow";
const labelCls = "text-xs uppercase tracking-[0.2em] font-semibold text-slate-500";
const buttonCls =
  "w-full flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white py-2.5 text-sm font-medium hover:bg-slate-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60";

/**
 * Completing an administrator-issued password reset (NFR-04, AZ-7 #136).
 *
 * The e-mailed link lands here. Completing it grants no session — the user
 * still signs in with the new password and their enrolled second factor,
 * exactly like any other login — and it revokes every existing session.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password changed. Sign in with it now.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8">
      <div className="w-full max-w-sm bg-white rounded-lg border border-slate-200 p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
            <BrandMark size={22} className="text-white" />
          </div>
          <span className="font-display font-semibold text-xl text-slate-900">BioLIMS</span>
        </div>

        <h1 className="text-2xl font-display font-semibold tracking-tight text-slate-900">
          Reset your password
        </h1>
        <p className="text-slate-500 mt-2 mb-6 text-sm">
          Choose a new password. It must be at least 12 characters and mix upper
          and lower case, a digit and a symbol. Every signed-in session ends when
          the change completes.
        </p>
        <form onSubmit={submit} className="space-y-4">
          {!params.get("token") && (
            <div>
              <label className={labelCls} htmlFor="reset-token">Reset code</label>
              <input data-testid="reset-token" id="reset-token" value={token} required
                onChange={(e) => setToken(e.target.value)}
                className={`${inputCls} font-mono text-xs`} />
            </div>
          )}
          <div>
            <label className={labelCls} htmlFor="reset-password">New password</label>
            <input data-testid="reset-password" id="reset-password" type="password" value={password}
              required onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="reset-confirm">Confirm password</label>
            <input data-testid="reset-confirm" id="reset-confirm" type="password" value={confirm}
              required onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="reset-submit" disabled={loading} className={buttonCls}>
            {loading && <SpinnerGap size={18} className="animate-spin" />}
            Change password
          </button>
        </form>
      </div>
    </div>
  );
}
